import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/pro-regular-svg-icons';
import { useRemoveMemberMutation } from '@hooks/requests/queries/use_remove_member_mutation';
import { useToast } from '@hooks/useToast';
import { describeMutationError } from '@hooks/requests/utils/_partner_api_client';
import type { DashboardMember } from '@hooks/requests/queries/use_partner_team_query';
import { Modal, Button, Alert } from '../ui';

interface RemoveConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  member: DashboardMember | null;
}

const memberLabel = (member: DashboardMember): string =>
  member.name || member.email || member.userId;

/**
 * Remove member confirmation modal (FR-016/017/018, Figma 799:2899). A 404
 * on submit (FR-021) — the member was already removed by someone else —
 * surfaces through the same plain error path as any other failure; the row
 * itself drops off on the table's next refetch, which
 * `useRemoveMemberMutation` triggers regardless of outcome.
 */
const RemoveConfirmationModal: React.FC<RemoveConfirmationModalProps> = ({
  isOpen,
  onClose,
  member,
}) => {
  const [error, setError] = useState('');
  const removeMutation = useRemoveMemberMutation();
  const { showToast } = useToast();

  const handleClose = () => {
    setError('');
    onClose();
  };

  const handleRemove = async () => {
    if (!member) return;
    setError('');

    try {
      await removeMutation.mutateAsync({ memberId: member.userId });
      showToast(`${memberLabel(member)} no longer has access`);
      handleClose();
    } catch (err) {
      setError(
        describeMutationError(err, {
          notFound: `${memberLabel(member)} no longer exists in this organization.`,
          fallback: 'Failed to remove the member',
        })
      );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Remove member"
      headerVariant="tinted"
      className="api-keys-modal"
      bodyClassName="api-keys-modal__body"
      footerClassName="api-keys-modal__foot"
      footer={
        <div className="flex gap-3 justify-end">
          <Button variant="secondary" className="au-modal-pill-btn--cancel" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="danger"
            className="au-modal-pill-btn--danger"
            leftIcon={<FontAwesomeIcon icon={faTrash} />}
            onClick={handleRemove}
            isLoading={removeMutation.isPending}
          >
            Remove this member
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {member && (
          <p className="au-modal-context-line">
            <span className="font-semibold">{memberLabel(member)}</span> will lose access to this
            workspace immediately. This action cannot be undone.
          </p>
        )}

        {error && <Alert variant="danger">{error}</Alert>}
      </div>
    </Modal>
  );
};

export default RemoveConfirmationModal;
